
import * as _ from 'lodash'
import {PAdbContext} from '../PA.Core/PAdbContext'
import {WB_CustomerRow, WB_FinishedProductRow, WB_ProductGroupRow} from '../PA.Core/PAdbSchemaRows'
import {IHaveExecutionBounds} from '../PA.Core/PA.Core.IF.Aspects'
import {ASync} from "../PA.Core/Basic/ASync";
import {BehaviorSubject} from "rxjs/BehaviorSubject";
import {IDateRange, IIdentityProvider} from "../PA.Core/PA.Core.IF.Basic";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {PlanningDataUtils} from "../PA.TPM.Core/PlanningDataUtils";
import {DateUtils} from "../PA.Core/Basic/Dates";

export interface ManualExecutionBoundsCreateOptions {
  dbContext?: PAdbContext,
  bPopulateWithPlanningDataBounds?: boolean,
  bIncludeProducts?: boolean,
  ProductsProvider?: IManualExecutionBoundsProductsProvider
}

export interface IManualExecutionBoundsProductsProvider {
  getProducts(dbContext: PAdbContext,
              WB_PlanningCustomerRows: WB_CustomerRow[],
              WB_SourceCustomerRows: WB_CustomerRow[],
              WB_ProductGroupRows: WB_ProductGroupRow[],
              Effective_Start_Dt: Date,
              Effective_End_Dt: Date)  :Promise<WB_FinishedProductRow[]>
}

export class ManualExecutionBounds
  implements IHaveExecutionBounds
{
  private _dbContext: PAdbContext
  private _options: ManualExecutionBoundsCreateOptions

  private _pauseChangeNotifications: boolean
  private _queuedChangeNotification: boolean
  private _queuedChanges: boolean

  private _bIHaveExecutionBoundsLoaded: boolean;
  private _OnIHaveExecutionBoundsLoaded: Promise<IHaveExecutionBounds>;

  private _Identity: IIdentityProvider;

  private _ExecutionBoundsChanged: BehaviorSubject<IHaveExecutionBounds>;

  private _PlanningCustomers: WB_CustomerRow[];
  private _SourceCustomers: WB_CustomerRow[];

  private _ProductGroups: WB_ProductGroupRow[];
  private _FinishedProducts: WB_FinishedProductRow[];

  private _InStore: IDateRange;
  private _InStore_Aligned: IDateRange;
  private _Shipment: IDateRange;
  private _Shipment_Aligned: IDateRange;

  private _IManualExecutionBoundsProductsProvider: IManualExecutionBoundsProductsProvider

  constructor(options?: ManualExecutionBoundsCreateOptions){
    let opts = options || {}
    this._options = opts

    if('dbContext' in opts){
      this._dbContext = opts.dbContext
    } else {
      this._dbContext = new PAdbContext()
    }

    this._pauseChangeNotifications = false
    this._queuedChangeNotification = false

    this._bIHaveExecutionBoundsLoaded = false
    this._OnIHaveExecutionBoundsLoaded = undefined
    this._Identity = new IdentityProvider()
    this._ExecutionBoundsChanged = new BehaviorSubject<IHaveExecutionBounds>(undefined)
    this._PlanningCustomers = []
    this._SourceCustomers = []
    this._ProductGroups = []
    this._FinishedProducts = []
    this._InStore = {StartDt: undefined, EndDt: undefined}
    this._InStore_Aligned = {StartDt: undefined, EndDt: undefined}
    this._Shipment = {StartDt: undefined, EndDt: undefined}
    this._Shipment_Aligned = {StartDt: undefined, EndDt: undefined}

    if('ProductsProvider' in opts){
      this._IManualExecutionBoundsProductsProvider= opts.ProductsProvider
    }

    if(opts.bPopulateWithPlanningDataBounds){
      this._OnIHaveExecutionBoundsLoaded = this.loadWithPlanningData()
    } else {
      this._bIHaveExecutionBoundsLoaded = true
    }
  }

  get ManualExecutionBoundsProductsProvider(): IManualExecutionBoundsProductsProvider {
    return this._IManualExecutionBoundsProductsProvider
  }

  set ManualExecutionBoundsProductsProvider(value) {
    this._IManualExecutionBoundsProductsProvider = value
  }

  get dbContext(): PAdbContext {
    return this._dbContext
  }

  set dbContext(value) {
    this._dbContext = value
  }

  get bIHaveExecutionBoundsLoaded(): boolean {
    return this._bIHaveExecutionBoundsLoaded
  }

  get OnIHaveExecutionBoundsLoaded(): Promise<IHaveExecutionBounds> {
    if(_.isEmpty(this._OnIHaveExecutionBoundsLoaded)) {
      return ASync.resolve(this)
    } else {
      return this._OnIHaveExecutionBoundsLoaded
    }
  }

  get Execution_Bounds_Identity(): IIdentityProvider {
    return this._Identity
  }

  set Execution_Bounds_Identity(value: IIdentityProvider) {
    this._Identity = value
  }

  get ExecutionBoundsChanged(): BehaviorSubject<IHaveExecutionBounds> {
    return this._ExecutionBoundsChanged
  }

  get Execution_PlanningCustomers(): WB_CustomerRow[] {
    return this._PlanningCustomers
  }

  set Execution_PlanningCustomers(aWBCsts: WB_CustomerRow[]) {
    this._PlanningCustomers = aWBCsts || []
    this.notifyChanges()
  }


  get Execution_SourceCustomers(): WB_CustomerRow[] {
    return this._SourceCustomers
  }

  set Execution_SourceCustomers(aWBCsts: WB_CustomerRow[]) {
    this._SourceCustomers = aWBCsts || []
    this.notifyChanges()
  }


  get Execution_ProductGroups(): WB_ProductGroupRow[] {
    return this._ProductGroups
  }

  set Execution_ProductGroups(aWBPrdGrps: WB_ProductGroupRow[]) {
    this._ProductGroups = aWBPrdGrps || []
    this.notifyChanges()
  }



  get Execution_FinishedProducts(): WB_FinishedProductRow[]{
    return this._FinishedProducts
  }

  set Execution_FinishedProducts(aWBPrds: WB_FinishedProductRow[]){
    this._FinishedProducts = aWBPrds || []
    this.notifyChanges()
  }


  get Execution_InStore(): IDateRange {
    return this._InStore
  }

  set Execution_InStore(aDateRange: IDateRange) {
    this._InStore = aDateRange
    this.setInStoreAlignment()
    this.notifyChanges()
  }

  get Execution_InStoreStartDt(): Date {
    return this._InStore.StartDt
  }

  set Execution_InStoreStartDt(aDate: Date) {
    let rng = this._InStore || {StartDt: undefined, EndDt: undefined}
    rng.StartDt = aDate
    if(!_.isDate(rng.EndDt)) { rng.EndDt = aDate }
    this.Execution_InStore = rng
  }

  get Execution_InStoreEndDt(): Date {
    return this._InStore.EndDt
  }

  set Execution_InStoreEndDt(aDate: Date) {
    let rng = this._InStore || {StartDt: undefined, EndDt: undefined}
    rng.EndDt = aDate
    if(!_.isDate(rng.StartDt)) { rng.StartDt = aDate }
    this.Execution_InStore = rng
  }


  get Execution_InStore_Aligned(): IDateRange {
    return this._InStore_Aligned
  }

  set Execution_InStore_Aligned(aDateRange: IDateRange) {
    this._InStore_Aligned = aDateRange
    this.notifyChanges()
  }



  get Execution_Shipment(): IDateRange {
    return this._Shipment
  }

  set Execution_Shipment(aDateRange: IDateRange) {
    this._Shipment = aDateRange
    this.setShipmentAlignment()
    this.notifyChanges()
  }


  get Execution_Shipment_Aligned(): IDateRange {
    return this._Shipment_Aligned
  }

  set Execution_Shipment_Aligned(aDateRange: IDateRange) {
    this._Shipment_Aligned = aDateRange
    this.notifyChanges()
  }


  pauseChangeNotifications(): void {
    this._pauseChangeNotifications = true
  }

  resumeChangeNotifications(): void {
    this._pauseChangeNotifications = false

    if(this._queuedChangeNotification){
      this.notifyChanges()
    }
  }

  notifyChanges(): void {
    const self = this

    if(self._pauseChangeNotifications){
      self._queuedChangeNotification = true
    } else {
      console.log("Manual Execution Bounds changed.")
      self._queuedChangeNotification = false
      self._ExecutionBoundsChanged.next(self)
    }
  }

  private setInStoreAlignment(): void {
    this._InStore_Aligned = _.clone(this._InStore)
    if(!_.isDate(this._InStore_Aligned.StartDt) || !_.isDate(this._InStore_Aligned.EndDt)){
      return
    }

    let durationInDays = Math.max(DateUtils.durationInDays(this._InStore_Aligned),1)
    let durationInWeeks = Math.ceil(durationInDays/7)
    this._InStore_Aligned.EndDt = DateUtils.addDays(this._InStore_Aligned.StartDt, (durationInWeeks * 7) - 1 )

  }

  private setShipmentAlignment(): void {
    this._Shipment_Aligned = _.clone(this._Shipment)
  }



  private loadWithPlanningData(): Promise<IHaveExecutionBounds> {
    const self = this
    return new Promise<IHaveExecutionBounds>((resolve, error) => {

      self.pauseChangeNotifications()
      PlanningDataUtils.getAllPlanningCustomers(self._dbContext)
        .then((wbPlanningCustomers: WB_CustomerRow[]) => {
          if(_.isEmpty(wbPlanningCustomers)){
            self.Execution_PlanningCustomers = []
          } else {
            //self.Execution_PlanningCustomers[0].cif.CUSTOMER_NAME
            self.Execution_PlanningCustomers = _.orderBy( wbPlanningCustomers,'CUSTOMER_NAME')
          }

          return PlanningDataUtils.getSourceCustomers(self._dbContext, self._PlanningCustomers)
        })
        .then((wbSourceCustomers: WB_CustomerRow[]) => {
          if(_.isEmpty(wbSourceCustomers)){
            self.Execution_SourceCustomers = []
          } else {
            self.Execution_SourceCustomers = _.orderBy( wbSourceCustomers,'CUSTOMER_NAME')
          }

          return PlanningDataUtils.getAllProductGroups(self._dbContext)
        })
        .then((wbProductGroups: WB_ProductGroupRow[]) => {
          if(_.isEmpty(wbProductGroups)){
            self.Execution_ProductGroups = []
          } else {
            self.Execution_ProductGroups = _.orderBy( wbProductGroups, 'PRODUCT_GROUP_NAME')
          }

          if(self._options.bIncludeProducts) {
            return PlanningDataUtils.getAllProducts(self._dbContext)
          } else {
            return ASync.resolve([])
          }
        })
        .then((wbProducts: WB_FinishedProductRow[]) => {
          if(_.isEmpty(wbProducts)){
            self.Execution_FinishedProducts = []
          } else {
            self.Execution_FinishedProducts = _.orderBy(wbProducts, 'PRODUCT_NAME')
          }

          return PlanningDataUtils.getDateBounds(self._dbContext)
        })
        .then((resDates: {InStore: IDateRange, Shipment: IDateRange}) => {
          if(_.isEmpty(resDates)){
            //
            self._InStore = {StartDt: new Date(1800,0,1), EndDt: new Date(9999,11,31)}
            self._InStore_Aligned = {StartDt: new Date(1800,0,1), EndDt: new Date(9999,11,31)}

            self._Shipment = {StartDt: new Date(1800,0,1), EndDt: new Date(9999,11,31)}
            self._Shipment_Aligned = {StartDt: new Date(1800,0,1), EndDt: new Date(9999,11,31)}
          } else {
            self._InStore = resDates.InStore
            self._InStore_Aligned = resDates.InStore
            self._Shipment = resDates.Shipment
            self._Shipment_Aligned = resDates.Shipment
          }
          self._bIHaveExecutionBoundsLoaded = true
          self.resumeChangeNotifications()
          resolve(self)
        })
        .catch((err) => {
          self.resumeChangeNotifications()
          error(err)
        })

    })
  }

}